/**
 * What the manager has selected on a server-paginated list.
 *
 * Two modes, because "select all 3,024 matching" cannot be a list of ids
 * on the client:
 *   { mode: 'ids', ids: Set }                        explicit rows
 *   { mode: 'filter', filter, excludeIds: Set }      everything matching the
 *                                                    filter frozen at the time
 *                                                    of "select all", minus
 *                                                    rows unticked since
 * Both serialise to the API's CityPlanMissionSelectionInput.
 */

export const SELECTION_MODE = { IDS: 'ids', FILTER: 'filter' }

export const emptySelection = () => ({
  mode: SELECTION_MODE.IDS,
  ids: new Set(),
})

/** @returns {boolean} */
export const isSelected = (selection, id) => {
  const key = String(id)
  if (selection.mode === SELECTION_MODE.FILTER)
    return !selection.excludeIds.has(key)
  return selection.ids.has(key)
}

/** Ticks or unticks one row. */
export const toggleId = (selection, id) => {
  const key = String(id)
  if (selection.mode === SELECTION_MODE.FILTER) {
    const excludeIds = new Set(selection.excludeIds)
    if (excludeIds.has(key)) excludeIds.delete(key)
    else excludeIds.add(key)
    return { ...selection, excludeIds }
  }
  const ids = new Set(selection.ids)
  if (ids.has(key)) ids.delete(key)
  else ids.add(key)
  return { ...selection, ids }
}

/** Ticks every row of the current page (ids mode). */
export const selectIds = (selection, ids) => {
  if (selection.mode === SELECTION_MODE.FILTER) {
    const excludeIds = new Set(selection.excludeIds)
    ids.forEach(id => excludeIds.delete(String(id)))
    return { ...selection, excludeIds }
  }
  const next = new Set(selection.ids)
  ids.forEach(id => next.add(String(id)))
  return { ...selection, ids: next }
}

/** "Select all N matching": freezes a copy of the filter. */
export const selectAllMatching = filter => ({
  mode: SELECTION_MODE.FILTER,
  filter: JSON.parse(JSON.stringify(filter || {})),
  excludeIds: new Set(),
})

export const clearSelection = () => emptySelection()

/**
 * @param {object} selection
 * @param {number} totalCount Rows matching the frozen filter
 * @returns {number}
 */
export const selectionCount = (selection, totalCount = 0) =>
  selection.mode === SELECTION_MODE.FILTER
    ? Math.max(0, (totalCount || 0) - selection.excludeIds.size)
    : selection.ids.size

/** @returns {boolean} */
export const isSelectionEmpty = (selection, totalCount = 0) =>
  selectionCount(selection, totalCount) === 0

/**
 * The API input.
 * @returns {{missionIds?: Array<string>, filter?: object, excludeIds?: Array<string>}}
 */
export const toSelectionInput = selection => {
  if (selection.mode === SELECTION_MODE.FILTER) {
    const input = { filter: selection.filter }
    if (selection.excludeIds.size > 0)
      input.excludeIds = Array.from(selection.excludeIds)
    return input
  }
  return { missionIds: Array.from(selection.ids) }
}

/**
 * "12 selected" / "All 3,024 matching (2 excluded)".
 * @returns {string}
 */
export const selectionToLabel = (selection, totalCount = 0) => {
  const count = selectionCount(selection, totalCount)
  if (selection.mode === SELECTION_MODE.FILTER) {
    const excluded = selection.excludeIds.size
    return `All ${count.toLocaleString()} matching${
      excluded ? ` (${excluded} excluded)` : ''
    }`
  }
  return `${count.toLocaleString()} selected`
}
